import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { AiEditRequestDto } from '@domain';
import { environment } from '@environments';
import { delay, Observable, of } from 'rxjs';
import { AiEditMockService } from './ai-edit-mock.service';

/** Latência do "servidor" no mock: o estado 1 (Enviando) precisa aparecer. */
const MOCK_LATENCY_MS = 350;

/**
 * Responde localmente os endpoints do modo "Editar com IA" quando `environment.AI_EDIT_MOCK`
 * está ligado, até a API existir:
 *
 * - `POST document/ai-edit` → 202 `{ taskId }`, e o roteiro de eventos começa;
 * - `POST document/ai-edit/:taskId/revert` → 200, e as alterações voltam.
 *
 * Todo o resto passa direto. Desligar o flag é o único passo para usar a API real.
 */
@Injectable()
export class AiEditMockInterceptor implements HttpInterceptor {
    // Resolvido só quando um pedido do modo IA passa por aqui: o mock depende do estado do
    // editor, que não deve nascer junto com o primeiro request do app.
    private _injector = inject(Injector);

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!environment.AI_EDIT_MOCK || request.method !== 'POST') return next.handle(request);

        const base = `${environment.API_BASE}document/ai-edit`;
        const url = request.url.split('?')[0];

        if (url === base) {
            const taskId = this._mock().start(request.body as AiEditRequestDto);
            return of(new HttpResponse({ status: 202, body: { taskId } })).pipe(delay(MOCK_LATENCY_MS));
        }

        const revert = url.startsWith(`${base}/`) && url.endsWith('/revert');
        if (revert) {
            const taskId = decodeURIComponent(url.slice(base.length + 1, -'/revert'.length));
            this._mock().revert(taskId);
            return of(new HttpResponse({ status: 200, body: {} })).pipe(delay(MOCK_LATENCY_MS));
        }

        return next.handle(request);
    }

    private _mock(): AiEditMockService {
        return this._injector.get(AiEditMockService);
    }
}
